import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, chakra, Flex, Heading, Text } from '@chakra-ui/react';
import type { FormSubmission, SubmissionStatus } from '@denise-augusta/domain';
import { executeAction } from '@/lattice/runtime';
import { usePortalUser } from '@/auth/roles';

type Filter = 'new' | 'all' | 'archived';

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'new', label: 'New' },
  { id: 'all', label: 'All' },
  { id: 'archived', label: 'Archived' },
];

const STATUS_STYLES: Record<SubmissionStatus, { bg: string; color: string; label: string }> = {
  new: { bg: '#FBEDEB', color: 'brand.terracotta', label: 'New' },
  read: { bg: 'brand.wash', color: 'brand.mid', label: 'Read' },
  archived: { bg: 'paper.warm', color: 'ink.muted', label: 'Archived' },
};

/**
 * Inquiry inbox — everything submitted through the public contact form.
 *
 * Each inquiry is emailed to Denise the moment it arrives; this is the
 * durable copy, so nothing is lost to a spam filter or a failed send.
 */
export function Submissions() {
  const user = usePortalUser();
  const [submissions, setSubmissions] = useState<FormSubmission[] | null>(null);
  const [filter, setFilter] = useState<Filter>('new');
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Which inquiries were unread when this view loaded. Opening one marks it
   * read, and without this it would vanish out from under you mid-sentence —
   * so the New list stays put until you refresh.
   */
  const [unreadOnLoad, setUnreadOnLoad] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const loaded = await executeAction<FormSubmission[]>(
        'form-submissions:listSubmissions',
        {},
        user
      );
      setSubmissions(loaded);
      setUnreadOnLoad(
        new Set(loaded.filter((item) => item.status === 'new').map((item) => item.path))
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load inquiries');
      setSubmissions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    if (!submissions) return [];
    if (filter === 'all') return submissions.filter((item) => item.status !== 'archived');
    if (filter === 'archived') return submissions.filter((item) => item.status === 'archived');
    return submissions.filter(
      (item) =>
        item.status !== 'archived' && (item.status === 'new' || unreadOnLoad.has(item.path))
    );
  }, [submissions, filter, unreadOnLoad]);

  const newCount = submissions?.filter((item) => item.status === 'new').length ?? 0;

  /** Optimistic — the blob write is a metadata-only PUT and effectively instant. */
  const changeStatus = async (submission: FormSubmission, status: SubmissionStatus) => {
    setBusyPath(submission.path);
    setError(null);
    try {
      await executeAction('form-submissions:setSubmissionStatus', { path: submission.path, status }, user);
      setSubmissions((current) =>
        (current ?? []).map((item) => (item.path === submission.path ? { ...item, status } : item))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the inquiry');
    } finally {
      setBusyPath(null);
    }
  };

  const remove = async (submission: FormSubmission) => {
    const who = `${submission.firstName} ${submission.lastName}`.trim();
    if (!window.confirm(`Permanently delete the inquiry from ${who}? This cannot be undone.`)) return;
    setBusyPath(submission.path);
    setError(null);
    try {
      await executeAction('form-submissions:deleteSubmission', { path: submission.path }, user);
      setSubmissions((current) => (current ?? []).filter((item) => item.path !== submission.path));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the inquiry');
    } finally {
      setBusyPath(null);
    }
  };

  const toggleOpen = (submission: FormSubmission) => {
    const opening = openPath !== submission.path;
    setOpenPath(opening ? submission.path : null);
    // Opening an inquiry is what marks it read — the same gesture as an inbox.
    if (opening && submission.status === 'new') void changeStatus(submission, 'read');
  };

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap="1rem" mb="1.6rem">
        <Box>
          <Heading fontFamily="heading" fontSize="2.2rem" fontWeight="300" color="brand.ink" mb="0.4rem">
            Inquiries
          </Heading>
          <Text fontSize="0.9rem" color="ink.soft">
            Everything sent through the contact form. Each one is emailed to you as it arrives —
            this is the permanent record.
          </Text>
        </Box>
        <chakra.button
          onClick={() => void load()}
          bg="paper.white"
          border="1px solid"
          borderColor="paper.border"
          color="ink.body"
          fontSize="0.72rem"
          fontWeight="500"
          letterSpacing="0.12em"
          textTransform="uppercase"
          px="1.4rem"
          py="0.6rem"
          borderRadius="20px"
          cursor="pointer"
          flexShrink="0"
          _hover={{ borderColor: 'brand.mid', color: 'brand.mid' }}
        >
          Refresh
        </chakra.button>
      </Flex>

      {error && (
        <Box
          bg="#FBEDEB"
          border="1px solid"
          borderColor="brand.terracotta"
          borderRadius="12px"
          px="1.4rem"
          py="1rem"
          mb="1.5rem"
        >
          <Text fontSize="0.85rem" color="brand.terracotta">
            {error}
          </Text>
        </Box>
      )}

      <Flex gap="0.5rem" mb="1.5rem" wrap="wrap">
        {FILTERS.map((option) => (
          <chakra.button
            key={option.id}
            onClick={() => setFilter(option.id)}
            bg={filter === option.id ? 'brand.ink' : 'paper.white'}
            color={filter === option.id ? 'paper.white' : 'ink.body'}
            border="1px solid"
            borderColor={filter === option.id ? 'brand.ink' : 'paper.border'}
            fontSize="0.72rem"
            fontWeight="500"
            letterSpacing="0.12em"
            textTransform="uppercase"
            px="1.4rem"
            py="0.6rem"
            borderRadius="20px"
            cursor="pointer"
            _hover={filter === option.id ? {} : { borderColor: 'brand.mid', color: 'brand.mid' }}
          >
            {option.label}
            {option.id === 'new' && newCount > 0 ? ` · ${newCount}` : ''}
          </chakra.button>
        ))}
      </Flex>

      {submissions === null ? (
        <Text fontSize="0.85rem" color="ink.soft">
          Loading…
        </Text>
      ) : visible.length === 0 ? (
        <EmptyState filter={filter} hasAny={submissions.length > 0} />
      ) : (
        <Flex direction="column" gap="0.9rem">
          {visible.map((submission) => (
            <SubmissionCard
              key={submission.path}
              submission={submission}
              open={openPath === submission.path}
              busy={busyPath === submission.path}
              onToggle={() => toggleOpen(submission)}
              onStatus={(status) => void changeStatus(submission, status)}
              onDelete={() => void remove(submission)}
            />
          ))}
        </Flex>
      )}
    </Box>
  );
}

function EmptyState({ filter, hasAny }: { filter: Filter; hasAny: boolean }) {
  const message =
    filter === 'archived'
      ? 'Nothing archived yet.'
      : filter === 'new' && hasAny
        ? "You're all caught up — no new inquiries."
        : 'No inquiries yet. They will appear here the moment someone sends the contact form.';

  return (
    <Box
      bg="paper.white"
      border="1px dashed"
      borderColor="paper.border"
      borderRadius="16px"
      px="1.8rem"
      py="3rem"
      textAlign="center"
    >
      <Text fontFamily="heading" fontStyle="italic" fontSize="1.05rem" color="ink.soft">
        {message}
      </Text>
    </Box>
  );
}

function SubmissionCard({
  submission,
  open,
  busy,
  onToggle,
  onStatus,
  onDelete,
}: {
  submission: FormSubmission;
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onStatus: (status: SubmissionStatus) => void;
  onDelete: () => void;
}) {
  const badge = STATUS_STYLES[submission.status];
  const name = `${submission.firstName} ${submission.lastName}`.trim();
  const received = new Date(submission.receivedAt);
  const mailto = `mailto:${submission.email}?subject=${encodeURIComponent(
    `Re: your message to Augusta Executive Solutions`
  )}`;

  return (
    <Box
      bg="paper.white"
      border="1px solid"
      borderColor={submission.status === 'new' ? 'brand.pale' : 'paper.border'}
      borderRadius="16px"
      opacity={busy ? 0.6 : 1}
      transition="opacity 0.15s"
    >
      <Flex
        as="button"
        // @ts-expect-error type passes through chakra to the button element
        type="button"
        onClick={onToggle}
        w="100%"
        textAlign="left"
        bg="transparent"
        border="none"
        cursor="pointer"
        px="1.5rem"
        py="1.2rem"
        gap="1rem"
        align="flex-start"
        justify="space-between"
      >
        <Box minW="0">
          <Flex align="center" gap="0.6rem" mb="0.25rem" wrap="wrap">
            <Text fontFamily="heading" fontSize="1.1rem" color="brand.ink">
              {name}
            </Text>
            <Box
              px="0.6rem"
              py="0.1rem"
              borderRadius="20px"
              fontSize="0.58rem"
              fontWeight="600"
              letterSpacing="0.14em"
              textTransform="uppercase"
              bg={badge.bg}
              color={badge.color}
            >
              {badge.label}
            </Box>
            {!submission.emailDelivered && (
              <Box
                px="0.6rem"
                py="0.1rem"
                borderRadius="20px"
                fontSize="0.58rem"
                fontWeight="600"
                letterSpacing="0.14em"
                textTransform="uppercase"
                bg="#FBEDEB"
                color="brand.terracotta"
                title={submission.emailError ?? 'The notification email did not go out.'}
              >
                Not emailed
              </Box>
            )}
          </Flex>
          <Text fontSize="0.8rem" color="ink.soft" mb="0.4rem">
            {[submission.email, submission.phone, submission.company]
              .filter(Boolean)
              .join(' · ')}{' '}
            · {received.toLocaleString()}
          </Text>
          {!open && (
            <Text fontSize="0.85rem" color="ink.body" lineClamp={1}>
              {submission.message}
            </Text>
          )}
        </Box>
        <Text fontSize="0.9rem" color="ink.muted" flexShrink="0" mt="0.3rem">
          {open ? '▲' : '▼'}
        </Text>
      </Flex>

      {open && (
        <Box px="1.5rem" pb="1.4rem">
          <Box
            bg="paper.warm"
            borderLeft="3px solid"
            borderLeftColor="brand.mid"
            borderRadius="8px"
            px="1.2rem"
            py="1rem"
            mb="1.2rem"
            fontSize="0.88rem"
            color="ink.body"
            lineHeight="1.7"
            whiteSpace="pre-wrap"
          >
            {submission.message}
          </Box>

          {submission.emailError && (
            <Text fontSize="0.75rem" color="brand.terracotta" mb="1rem">
              Notification email failed: {submission.emailError}
            </Text>
          )}

          <Flex gap="0.6rem" wrap="wrap">
            <CardAction as="a" href={mailto} primary>
              Reply by email
            </CardAction>
            {submission.phone && (
              <CardAction as="a" href={`tel:${submission.phone.replace(/[^\d+]/g, '')}`}>
                Call {submission.phone}
              </CardAction>
            )}
            {submission.status !== 'archived' ? (
              <CardAction onClick={() => onStatus('archived')}>Archive</CardAction>
            ) : (
              <CardAction onClick={() => onStatus('read')}>Unarchive</CardAction>
            )}
            {submission.status === 'read' && (
              <CardAction onClick={() => onStatus('new')}>Mark unread</CardAction>
            )}
            <CardAction onClick={onDelete} danger>
              Delete
            </CardAction>
          </Flex>
        </Box>
      )}
    </Box>
  );
}

function CardAction({
  children,
  onClick,
  href,
  as,
  primary,
  danger,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  as?: 'a';
  primary?: boolean;
  danger?: boolean;
}) {
  const styles = {
    fontSize: '0.7rem',
    fontWeight: '500',
    letterSpacing: '0.12em',
    textTransform: 'uppercase' as const,
    px: '1.3rem',
    py: '0.6rem',
    borderRadius: '20px',
    cursor: 'pointer',
    textDecoration: 'none',
    textAlign: 'center' as const,
    border: '1px solid',
    bg: primary ? 'brand.mid' : 'paper.white',
    color: primary ? 'paper.white' : danger ? 'brand.terracotta' : 'ink.body',
    borderColor: primary ? 'brand.mid' : danger ? 'brand.terracotta' : 'paper.border',
    _hover: primary
      ? { bg: 'brand.ink', borderColor: 'brand.ink' }
      : danger
        ? { bg: '#FBEDEB' }
        : { borderColor: 'brand.mid', color: 'brand.mid' },
  };

  if (as === 'a') {
    return (
      <chakra.a href={href} {...styles}>
        {children}
      </chakra.a>
    );
  }
  return (
    <chakra.button type="button" onClick={onClick} {...styles}>
      {children}
    </chakra.button>
  );
}
